import React, { useState, useContext, useEffect } from "react";
import { AppContext } from "../../../../appContext";
import { DashboardContext } from "../../DashboardContext";
import { getFutureSessions } from "../../../../api/sessionApi";
import { ISession } from "../../../../types/dashboard";
import "./Task.css";
import TrashIcon from '../../../../assets/notesEditor/trash.svg';
import ChainIcon from '../../../../assets/notesEditor/chain.svg';

interface ITaskProps {
    data: {
        id?: string;
        title: string;
        text: string;
        label?: string;
        tags?: string[];
        checked: boolean;
        datetime?: number;
        hasDate?: boolean;
        sessionId?: string;
        client?: string;
    };
    isOpen?: boolean;
    onToggle: () => void;
    onCheck: () => void;
    onRemove: () => void;
    onSave?: (updatedData: { 
        title: string; 
        text: string; 
        tags?: string[]; 
        datetime?: number; 
        hasDate?: boolean;
        client?: string;
        sessionId?: string;
    }) => void;
    minimal?: boolean;
    selectedClientId?: string;
    selectedClientName?: string;
}

export const Task = ({ data, isOpen, onToggle, onCheck, onRemove, onSave, minimal = false, selectedClientId, selectedClientName }: ITaskProps) => {
    const { translate } = useContext(AppContext);
    const dashboardContext = useContext(DashboardContext);
    const { selectedClient: contextClient } = dashboardContext || {};
    
    // Use props if provided (meeting page), otherwise use context (dashboard)
    const selectedClient = selectedClientId 
        ? { id: selectedClientId, name: selectedClientName || '' }
        : contextClient;
    const [isEditing, setIsEditing] = useState(false);
    const [editTitle, setEditTitle] = useState(data.title);
    const [editText, setEditText] = useState(data.text);
    const [editDatetime, setEditDatetime] = useState<string>('');
    const [editTags, setEditTags] = useState<string[]>(data.tags || []);
    const [newTag, setNewTag] = useState('');
    const [hasDateChecked, setHasDateChecked] = useState(data.hasDate || false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [boundToClient, setBoundToClient] = useState(!!data.client);
    const [selectedSessionId, setSelectedSessionId] = useState<string | undefined>(data.sessionId);
    const [futureSessions, setFutureSessions] = useState<ISession[]>([]);
    const [loadingSessions, setLoadingSessions] = useState(false);

    useEffect(() => {
        if (data.datetime && data.hasDate) {
            const date = new Date(data.datetime * 1000);
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            const hours = String(date.getHours()).padStart(2, '0');
            const minutes = String(date.getMinutes()).padStart(2, '0');
            setEditDatetime(`${year}-${month}-${day}T${hours}:${minutes}`);
        }
    }, [data.datetime, data.hasDate]);

    useEffect(() => {
        const fetchSessions = async () => {
            if (boundToClient && selectedClient?.id) {
                setLoadingSessions(true);
                try {
                    const sessions = await getFutureSessions(selectedClient.id);
                    setFutureSessions(sessions);
                } catch (error) {
                    console.error('Failed to fetch sessions:', error);
                    setFutureSessions([]);
                } finally {
                    setLoadingSessions(false);
                }
            } else {
                setFutureSessions([]);
                setSelectedSessionId(undefined);
            }
        };
        
        fetchSessions();
    }, [boundToClient, selectedClient?.id]);

    const handleSave = () => {
        if (onSave) {
            let datetimeTimestamp: number | undefined = undefined;
            if (hasDateChecked && editDatetime) {
                const date = new Date(editDatetime);
                datetimeTimestamp = Math.floor(date.getTime() / 1000);
            }

            onSave({
                title: editTitle,
                text: editText,
                tags: editTags,
                datetime: datetimeTimestamp,
                hasDate: hasDateChecked,
                client: boundToClient && selectedClient?.id ? selectedClient.id : undefined,
                sessionId: selectedSessionId
            });
        }
        setIsEditing(false);
    };

    const handleCancel = () => {
        setEditTitle(data.title);
        setEditText(data.text);
        setEditTags(data.tags || []);
        setHasDateChecked(data.hasDate || false);
        if (data.datetime && data.hasDate) {
            const date = new Date(data.datetime * 1000);
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            const hours = String(date.getHours()).padStart(2, '0');
            const minutes = String(date.getMinutes()).padStart(2, '0');
            setEditDatetime(`${year}-${month}-${day}T${hours}:${minutes}`);
        }
        setIsEditing(false);
    };

    const handleEdit = (e: React.MouseEvent) => {
        e.stopPropagation();
        setIsEditing(true);
        if (!isOpen) {
            onToggle();
        }
    };

    const handleDeleteClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        setShowDeleteConfirm(true);
    };

    const confirmDelete = () => {
        setShowDeleteConfirm(false);
        onRemove();
    };

    const cancelDelete = () => {
        setShowDeleteConfirm(false);
    };

    const handleAddTag = () => {
        if (newTag.trim() && !editTags.includes(newTag.trim())) {
            setEditTags([...editTags, newTag.trim()]);
            setNewTag('');
        }
    };

    const handleRemoveTag = (tagToRemove: string) => {
        setEditTags(editTags.filter(tag => tag !== tagToRemove));
    };

    const handleTagKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleAddTag();
        }
    };

    const formatDateTime = (timestamp?: number) => {
        if (!timestamp) return '';
        const date = new Date(timestamp * 1000);
        if (isNaN(date.getTime())) return '';
        return date.toLocaleString(undefined, { 
            month: 'short', 
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const handleTaskClick = () => {
        onToggle();
    };

    return (
        <div className={`task ${isOpen ? 'task--open' : 'task--closed'} ${data.checked ? 'task--checked' : ''}`}>
            <div className="task_head" onClick={handleTaskClick}>
                <div 
                    className={`task_checkbox ${data.checked ? 'task_checkbox--checked' : ''}`}
                    onClick={(e) => {
                        e.stopPropagation();
                        onCheck();
                    }}
                />
                <div className="task_left">
                    <div className={`task_name ${data.checked ? 'task_name--checked' : ''}`}>
                        {data.sessionId && (
                            <img src={ChainIcon} alt={translate('task_assigned_to_session')} className="task_session_icon" />
                        )}
                        {data.title}
                    </div>
                    {!minimal && data.hasDate && data.datetime && (
                        <div className="task_datetime">
                            {formatDateTime(data.datetime)}
                        </div>
                    )}
                </div>
                
                {/* Display tags if available - only in non-minimal mode inline */}
                {!minimal && data.tags && data.tags.length > 0 && (
                    <div className="task_tags">
                        {data.tags.map((tag, idx) => (
                            <div key={idx} className="task_tag">
                                {tag}
                            </div>
                        ))}
                    </div>
                )}
                
                <div className="task_head_actions">
                    {isOpen && !isEditing && (
                        <div 
                            className="dashboard_main_block_edit_button" 
                            onClick={(e) => {
                                e.stopPropagation();
                                setIsEditing(true);
                            }}
                        />
                    )}
                    <div className="dashboard_main_block_open_button"></div>
                </div>
            </div>
            
            {/* Metadata row for minimal mode when expanded */}
            {minimal && isOpen && (data.hasDate && data.datetime || data.tags && data.tags.length > 0) && (
                <div className="task_metadata_row">
                    {data.hasDate && data.datetime && (
                        <div className="task_datetime">
                            {formatDateTime(data.datetime)}
                        </div>
                    )}
                    {data.tags && data.tags.length > 0 && (
                        <div className="task_tags">
                            {data.tags.map((tag, idx) => (
                                <div key={idx} className="task_tag">
                                    {tag}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
            <div className="task_more">
                {isEditing ? (
                    <div className="task_edit_form">
                        <input
                            type="text"
                            className="task_edit_title"
                            value={editTitle}
                            onChange={(e) => setEditTitle(e.target.value)}
                            placeholder={translate('tasks_title_placeholder') || 'Task title'}
                        />
                        <textarea
                            className="task_edit_text"
                            value={editText}
                            onChange={(e) => setEditText(e.target.value)}
                            placeholder={translate('tasks_description_placeholder') || 'Task description'}
                            rows={4}
                        />
                        
                        {/* Tags editing */}
                        <div className="task_edit_tags_group">
                            <label className="task_edit_label">
                                {translate('tasks_field_tags') || 'Tags'}
                            </label>
                            <div className="task_edit_tags_list">
                                {editTags.map((tag, idx) => (
                                    <div key={idx} className="task_edit_tag">
                                        <span>{tag}</span>
                                        <button
                                            type="button"
                                            className="task_edit_tag_remove"
                                            onClick={() => handleRemoveTag(tag)}
                                        >
                                            ×
                                        </button>
                                    </div>
                                ))}
                            </div>
                            <div className="task_edit_tag_input_group">
                                <input
                                    type="text"
                                    className="task_edit_tag_input"
                                    value={newTag}
                                    onChange={(e) => setNewTag(e.target.value)}
                                    onKeyPress={handleTagKeyPress}
                                    placeholder={translate('tasks_tag_placeholder') || 'Add a tag...'}
                                />
                                <button
                                    type="button"
                                    className="task_edit_tag_add_btn"
                                    onClick={handleAddTag}
                                >
                                    +
                                </button>
                            </div>
                        </div>

                        {/* DateTime checkbox and input */}
                        <div className="task_edit_datetime_group">
                            <label className="task_edit_datetime_checkbox_label">
                                <input
                                    type="checkbox"
                                    checked={hasDateChecked}
                                    onChange={(e) => setHasDateChecked(e.target.checked)}
                                    className="task_edit_datetime_checkbox"
                                />
                                <span>{translate('tasks_has_date') || 'Schedule for specific date/time'}</span>
                            </label>
                            {hasDateChecked && (
                                <input
                                    type="datetime-local"
                                    className="task_edit_datetime"
                                    value={editDatetime}
                                    onChange={(e) => setEditDatetime(e.target.value)}
                                />
                            )}
                        </div>

                        {/* Client binding checkbox */}
                        {selectedClient && (
                            <div className="task_edit_client_group">
                                <label className="task_edit_datetime_checkbox_label">
                                    <input
                                        type="checkbox"
                                        checked={boundToClient}
                                        onChange={(e) => setBoundToClient(e.target.checked)}
                                        className="task_edit_datetime_checkbox"
                                    />
                                    <span>{translate('tasks_bound_to_client') || 'Bound to Client'}</span>
                                </label>
                            </div>
                        )}

                        {/* Session selection dropdown */}
                        {boundToClient && selectedClient && (
                            <div className="task_edit_session_group">
                                <label className="task_edit_label">
                                    {translate('tasks_assign_to_session') || 'Assign to Session'}
                                </label>
                                {loadingSessions ? (
                                    <div className="task_edit_loading">{translate('common_loading') || 'Loading...'}</div>
                                ) : futureSessions.length > 0 ? (
                                    <select
                                        className="task_edit_session_select"
                                        value={selectedSessionId || ''}
                                        onChange={(e) => setSelectedSessionId(e.target.value || undefined)}
                                    >
                                        <option value="">{translate('tasks_session_none') || 'None'}</option>
                                        {futureSessions.map(session => (
                                            <option key={session.id} value={session.id || ''}>
                                                {new Date(session.date).toLocaleString(undefined, { 
                                                    month: 'short', 
                                                    day: 'numeric',
                                                    hour: '2-digit',
                                                    minute: '2-digit'
                                                })}
                                            </option>
                                        ))}
                                    </select>
                                ) : (
                                    <div className="task_edit_no_sessions">{translate('tasks_no_future_sessions') || 'No future sessions'}</div>
                                )}
                            </div>
                        )}

                        <div className="task_edit_actions">
                            <button className="task_edit_save" onClick={handleSave}>
                                {translate('common_save') || 'Save'}
                            </button>
                            <button className="task_edit_cancel" onClick={handleCancel}>
                                {translate('common_cancel') || 'Cancel'}
                            </button>
                            <button 
                                className="task_edit_delete" 
                                onClick={handleDeleteClick}
                                title={translate('tasks_delete') || 'Delete'}
                            >
                                <img src={TrashIcon} alt={translate('tasks_delete') || 'Delete'} className="task_delete_icon" />
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="task_more_text">
                        {data.text}
                    </div>
                )}
            </div>

            {/* Delete confirmation popup */}
            {showDeleteConfirm && (
                <div className="task_confirm_overlay" onClick={cancelDelete}>
                    <div className="task_confirm_dialog" onClick={(e) => e.stopPropagation()}>
                        <div className="task_confirm_message">
                            {translate('tasks_confirm_delete') || 'Are you sure you want to delete this task?'}
                        </div>
                        <div className="task_confirm_actions">
                            <button className="task_confirm_cancel" onClick={cancelDelete}>
                                {translate('common_cancel') || 'Cancel'}
                            </button>
                            <button className="task_confirm_delete" onClick={confirmDelete}>
                                {translate('common_delete') || 'Delete'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
